import React, { useContext, useState, useEffect } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import { IconButton, Tooltip } from "@material-ui/core";
import { MoreVert, Replay, Android } from "@material-ui/icons";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import TicketOptionsMenu from "../TicketOptionsMenu";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import SplynxDocumentModal from "../SplynxDocumentModal";
import UISPDocumentModal from "../UISPDocumentModal";

const useStyles = makeStyles(theme => ({
	actionButtons: {
		marginRight: 6,
		flex: "none",
		alignSelf: "center",
		marginLeft: "auto",
		display: "flex",
		alignItems: "center",
		gap: 8,
	},
	pillButton: {
		borderRadius: 20,
		textTransform: "none",
		fontWeight: 600,
		paddingLeft: 16,
		paddingRight: 16,
	},
	resolveButton: {
		borderRadius: 20,
		textTransform: "none",
		fontWeight: 600,
		paddingLeft: 18,
		paddingRight: 18,
		backgroundColor: "#22c55e",
		color: "#fff",
		"&:hover": {
			backgroundColor: "#16a34a",
		},
	},
	roundIconButton: {
		borderRadius: "50%",
	},
	aiButton: {
		borderRadius: 20,
		textTransform: "none",
		fontWeight: 600,
		backgroundColor: "#7c3aed",
		color: "#fff",
		"&:hover": {
			backgroundColor: "#6d28d9",
		},
	},
}));

const TicketActionButtons = ({ ticket }) => {
	const classes = useStyles();
	const history = useHistory();
	const [anchorEl, setAnchorEl] = useState(null);
	const [loading, setLoading] = useState(false);
	const [documentModalOpen, setDocumentModalOpen] = useState(false);
	const [activeCRM, setActiveCRM] = useState(null); // 'splynx' | 'uisp' | null
	const ticketOptionsMenuOpen = Boolean(anchorEl);
	const { user } = useContext(AuthContext);

	useEffect(() => {
		const fetchCRMSettings = async () => {
			try {
				const { data } = await api.get("/settings/crm-status");
				if (data.uispEnabled) {
					setActiveCRM("uisp");
				} else if (data.splynxEnabled) {
					setActiveCRM("splynx");
				} else {
					setActiveCRM(null);
				}
			} catch {
				setActiveCRM(null);
			}
		};
		fetchCRMSettings();
	}, []);

	const handleOpenTicketOptionsMenu = e => {
		setAnchorEl(e.currentTarget);
	};

	const handleCloseTicketOptionsMenu = e => {
		setAnchorEl(null);
	};

	const handleUpdateTicketStatus = async (e, status, userId) => {
		setLoading(true);
		try {
			await api.put(`/tickets/${ticket.id}`, {
				status: status,
				userId: userId || null,
			});

			setLoading(false);
			if (status === "open") {
				history.push(`/tickets/${ticket.id}`);
			} else {
				history.push("/tickets");
			}
		} catch (err) {
			setLoading(false);
			toastError(err);
		}
	};

	const handleTakeControlFromAI = async () => {
		setLoading(true);
		try {
			await api.put(`/tickets/${ticket.id}`, {
				aiActive: false,
				status: "open",
				userId: user?.id,
			});
			setLoading(false);
			history.push(`/tickets/${ticket.id}`);
		} catch (err) {
			setLoading(false);
			toastError(err);
		}
	};

	// Opens the Documentar / No Documentar modal
	const handleResolveClick = () => {
		setDocumentModalOpen(true);
	};

	// "No Documentar" path — close ticket normally
	const handleResolveDirect = async () => {
		setDocumentModalOpen(false);
		await handleUpdateTicketStatus(null, "closed", user?.id);
	};

	// Called after modal finishes (success or dismiss)
	const handleDocumentModalClose = () => {
		setDocumentModalOpen(false);
		history.push("/tickets");
	};

	return (
		<div className={classes.actionButtons}>
			{ticket.aiActive && ticket.status !== "closed" && (
				<Tooltip title={i18n.t("aiChat.tooltip")}>
					<ButtonWithSpinner
						loading={loading}
						startIcon={<Android />}
						size="small"
						variant="contained"
						className={classes.aiButton}
						onClick={handleTakeControlFromAI}
					>
						{i18n.t("aiChat.takingControl")}
					</ButtonWithSpinner>
				</Tooltip>
			)}
			{ticket.status === "closed" && (
				<ButtonWithSpinner
					loading={loading}
					startIcon={<Replay />}
					size="small"
					variant="outlined"
					className={classes.pillButton}
					onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
				>
					{i18n.t("messagesList.header.buttons.reopen")}
				</ButtonWithSpinner>
			)}
			{ticket.status === "open" && (
				<>
					<ButtonWithSpinner
						loading={loading}
						startIcon={<Replay />}
						size="small"
						variant="outlined"
						className={classes.pillButton}
						onClick={e => handleUpdateTicketStatus(e, "pending", null)}
					>
						{i18n.t("messagesList.header.buttons.return")}
					</ButtonWithSpinner>
					<ButtonWithSpinner
						loading={loading}
						size="small"
						variant="contained"
						className={classes.resolveButton}
						onClick={handleResolveClick}
					>
						{i18n.t("messagesList.header.buttons.resolve")}
					</ButtonWithSpinner>
					<IconButton className={classes.roundIconButton} onClick={handleOpenTicketOptionsMenu}>
						<MoreVert />
					</IconButton>
					<TicketOptionsMenu
						ticket={ticket}
						anchorEl={anchorEl}
						menuOpen={ticketOptionsMenuOpen}
						handleClose={handleCloseTicketOptionsMenu}
					/>
				</>
			)}
			{ticket.status === "pending" && (
				<ButtonWithSpinner
					loading={loading}
					size="small"
					variant="contained"
					className={classes.resolveButton}
					onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
				>
					{i18n.t("messagesList.header.buttons.accept")}
				</ButtonWithSpinner>
			)}

			{activeCRM === "uisp" ? (
				<UISPDocumentModal
					open={documentModalOpen}
					ticket={ticket}
					onClose={handleDocumentModalClose}
					onResolveDirect={handleResolveDirect}
				/>
			) : (
				<SplynxDocumentModal
					open={documentModalOpen}
					ticket={ticket}
					onClose={handleDocumentModalClose}
					onResolveDirect={handleResolveDirect}
				/>
			)}
		</div>
	);
};

export default TicketActionButtons;
